import { Link } from "react-router-dom";
import { mainSiteUrl } from "@/mocks/site";
import { articles } from "@/mocks/articles";

const faqs = [
  {
    q: "What is a Basetao Spreadsheet?",
    a: "A Basetao Spreadsheet is an organized list of product finds, links and research notes that helps shoppers browse, compare and keep track of items available through the Basetao shopping agent.",
  },
  {
    q: "How do I use the Basetao Spreadsheet to find products?",
    a: "Browse the spreadsheet by category, note the product links you are interested in, and use the organized structure to compare options before you order. The spreadsheet saves time compared to searching one product at a time.",
  },
  {
    q: "What should I check in Basetao QC photos?",
    a: "Focus on measurements, colors, stitching, logos and overall condition. Compare the QC photos against the original product listing and request additional photos whenever a detail looks unclear.",
  },
  {
    q: "How does Basetao shipping cost work?",
    a: "Shipping cost depends on factors like parcel weight, destination country and the shipping line you choose. Comparing routes and delivery times before you submit a parcel can help you control the final cost.",
  },
  {
    q: "Is Basetao legit and safe to use?",
    a: "Basetao is a well-known shopping agent, but safety also depends on your own research. Verify sellers, review QC photos carefully and check current policies before completing a purchase.",
  },
];

export default function ArticleFaq() {
  return (
    <>
      {/* Articles grid */}
      <section id="guides" className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="flex flex-col items-center text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
              Basetao Guides
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              Explore Our Basetao Pillar Articles
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground-600">
              Original, practical guides covering the spreadsheet, product finds, QC, shipping,
              safety, reviews and more. Pick a topic to keep learning.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/${article.slug}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-white transition-all hover:border-primary-300"
              >
                <div className="h-44 w-full overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={article.title}
                    className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <span className="inline-flex w-fit rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
                    {article.category}
                  </span>
                  <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950">
                    {article.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                    {article.excerpt}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-700">
                    Read article
                    <i
                      className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-0.5"
                      aria-hidden="true"
                    ></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="w-full bg-background-100 py-16 md:py-24">
        <div className="mx-auto w-full max-w-4xl px-4 md:px-6">
          <div className="flex flex-col items-center text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
              FAQ
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="mt-10 space-y-3">
            {faqs.map((faq) => (
              <div key={faq.q} className="rounded-lg border border-background-200 bg-white p-5">
                <h3 className="font-heading text-base font-semibold text-foreground-950">
                  {faq.q}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-4xl px-4 md:px-6">
          <div className="relative overflow-hidden rounded-lg bg-primary-500 px-6 py-12 text-center md:px-12 md:py-16">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-white md:text-4xl">
              Ready to Explore the Basetao Spreadsheet?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-primary-50">
              Start researching products, compare your options and discover the latest Basetao
              resources on the main website.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-white px-6 py-3.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100 sm:w-auto"
              >
                Explore Basetao Spreadsheet
                <i className="ri-arrow-right-up-line text-base" aria-hidden="true"></i>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-white/40 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 sm:w-auto"
              >
                Visit Main Website
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}